import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import type { Member } from '@scholarship-app/types';

/**
 * Loads the current user's member document from the active household.
 * Used to display the logged-in user's name in greetings and headers.
 *
 * Returns null while loading or if no membership exists.
 */
export function useCurrentMember() {
  const { user, membership } = useAuth();
  const [member, setMember] = useState<Member | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!db || !user || !membership) {
      setLoading(false);
      setMember(null);
      return;
    }
    const ref = doc(db, 'households', membership.householdId, 'members', user.uid);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          setMember(snap.data() as Member);
        } else {
          setMember(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error('[useCurrentMember] snapshot failed:', err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [user, membership]);

  /** First name extracted from display_name (e.g., "Cole Mullinax" → "Cole"). */
  const firstName = member?.display_name?.split(' ')[0] ?? '';

  return { member, firstName, loading };
}
